// src/api/axios_instance.js
import axios from 'axios';
import { isTokenExpired } from '../utils/auth';
import { onGetStorageItem, onRemoveStorageItem, onSetStorageItem, STORAGE_KEY } from './helper/storage';

const axios_instance = axios.create({
  baseURL: import.meta.env.VITE_API_SERVICE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

axios_instance.interceptors.request.use(
  async (config) => {
    const token = onGetStorageItem(STORAGE_KEY.TOKEN);
    if (token && !isTokenExpired(token)) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // Token is expired
      console.warn('Token expired. Redirecting to login...');
      // Optionally: redirect to login or trigger refresh
      // window.location.href = "/login";
    }

    return config;
  },
  (error) => Promise.reject(error),
);

export default axios_instance;

// Global response interceptor: industry-standard handling for auth/subscription
axios_instance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const message: string | undefined = error?.response?.data?.message || error?.message;

    // Preserve intended destination to come back after login/subscription
    const next = window.location.pathname + window.location.search;

    if (status === 401) {
      // Don't redirect or remove token for change-password endpoint
      // The user is authenticated, they just entered wrong current password
      const isChangePasswordEndpoint = error?.config?.url?.includes('/change-password');
      if (isChangePasswordEndpoint) {
        // For change password, just reject the error so the component can handle it
        // Don't remove token or redirect - user is still authenticated
        return Promise.reject(error);
      }

      // For other 401 errors, user is not authenticated - remove token and redirect
      try {
        onRemoveStorageItem(STORAGE_KEY.TOKEN);
      } catch (error) {
        console.error('Error removing token:', error);
      }
      // Only redirect to login if we're not already on the login page
      // This prevents redirect loops when login fails with invalid credentials
      const isOnLoginPage = window.location.pathname.includes('/auth/login');
      if (!isOnLoginPage) {
        // Redirect to login
        window.location.href = `/auth/login?next=${encodeURIComponent(next)}`;
      }
      return Promise.reject(error);
    }

    if (status === 403) {
      // Let UI handle modal + redirect instead of forcing a redirect here
      try {
        onSetStorageItem(STORAGE_KEY.SUBSCRIPTION_REDIRECT_REASON, message || 'subscription_required')
      } catch (error) {
        console.error('Error setting subscription redirect reason:', error);
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);
